import { ReactNode } from "react"
import Dynamic from '@/lib/dynamic'
import { NavigatorRouteProps } from "@/contexts/navigator"
import { APP_CAN_ACCESS_BACKEND } from "@bloomstack/apis/permissions"
import { USER_WORKSPACES_LIST, USER_AGENTS_LIST, USER_INTEGRATIONS_MANAGE, USER_TASKS_MANAGE } from "@bloomstack/apis/entityPermissions"
import { BlocksIcon, BotIcon, ShieldCheckIcon, BookOpenIcon, ListTodoIcon, MessagesSquareIcon, CircleUserRoundIcon } from "lucide-react"
import { UnreadNotificationsBadge } from "@/chat2/components/notifications/unreadBadge"
import { OnlineOfflineIndicator, OnlineOfflineLabelIndicator } from "@/components/ui/onlineOfflineIndicator"
import ComponentSpinner from "@/components/ui/componentSpinner"

// ============================================================================
// LAZY LOADED PANEL COMPONENTS
// Add new lazy-loaded panel components here
// ============================================================================

const WorkspaceNavPanelSkeleton = () => (
  <div className="flex flex-col w-full h-full">
    {/* Title bar placeholder — matches LeftPanel h-10 header */}
    <div className="flex flex-row items-center h-10 px-2 py-2 border-b border-b-black/10 dark:border-b-white/20 bg-zinc-200 dark:bg-zinc-700">
      <div className="h-4 w-24 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
    </div>
    {/* Search bar placeholder */}
    <div className="flex flex-row items-center gap-1 px-2 py-1 border-b border-gray-200 dark:border-gray-700">
      <div className="h-8 grow rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
      <div className="w-8 h-8 rounded bg-gray-200 dark:bg-gray-700 animate-pulse shrink-0" />
    </div>
    {/* Card placeholders */}
    <div className="flex flex-col gap-0">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex flex-row gap-2 p-2 items-center animate-pulse border-b border-white/10">
          <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 shrink-0" />
          <div className="flex flex-col gap-1 grow">
            <div className="h-3 w-3/4 rounded bg-gray-300 dark:bg-gray-600" />
            <div className="h-2 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      ))}
    </div>
  </div>
);
const WorkspaceNavPanel = Dynamic(() => import("@/chat2/components/navPanels/workspaceNavPanel").then(mod => ({ default: mod.WorkspaceNavPanel })), { ssr: false, loading: () => <WorkspaceNavPanelSkeleton /> })
const AgentsNavPanel2 = Dynamic(() => import("@/chat2/components/navPanels/agentNavPanel").then(mod => ({ default: mod.AgentsNavPanel2 })), { ssr: false, loading: () => <ComponentSpinner /> })
const NotificationsPanel2 = Dynamic(() => import("@/chat2/components/navPanels/notificationsPanel").then(mod => ({ default: mod.NotificationsPanel2 })), { ssr: false, loading: () => <ComponentSpinner /> })
const IntegrationsPanel2 = Dynamic(() => import("@/components/workspace/integrations/integrationsPanel2").then(mod => ({ default: mod.IntegrationsPanel2 })), { ssr: false, loading: () => <ComponentSpinner /> })
// WebAppPanel removed - apps feature disabled for now
// const WebAppPanel = Dynamic(() => import("@/components/workspace/apps/webAppPanel").then(mod => ({ default: mod.WebAppPanel })), { ssr: false, loading: () => <ComponentSpinner /> })
const AdminNavPanel = Dynamic(() => import("@/chat2/components/navPanels/adminNavPanel").then(mod => ({ default: mod.AdminNavPanel })), { ssr: false, loading: () => <ComponentSpinner /> })
const WikiNavPanel = Dynamic(() => import("@/components/wiki/WikiNavPanel").then(mod => ({ default: mod.WikiNavPanel })), { ssr: false, loading: () => <ComponentSpinner /> })
const ProfileNavPanel = Dynamic(() => import("@/chat2/components/profile/profileNavPanel").then(mod => ({ default: mod.ProfileNavPanel })), { ssr: false, loading: () => <ComponentSpinner /> })
const TasksNavPanel = Dynamic(() => import("@/chat2/components/navPanels/tasks/tasksPanel").then(mod => ({ default: mod.TasksPanel })), { ssr: false, loading: () => <ComponentSpinner /> })

// ============================================================================
// TYPES
// ============================================================================

export type WorkspaceToolPanel = {
  id: string
  label: string | ReactNode
  toolTip?: string | ReactNode
  icon: ReactNode
  panel?: ReactNode
  order: number
  pushDown?: boolean
  requiredPermission?: string[]
  onNavigatorDriverTrigger?: (route: NavigatorRouteProps) => boolean
}

// ============================================================================
// PANEL DEFINITIONS
// ============================================================================

/**
 * Build navigation panel definitions.
 * 
 * @param user - Current user (needed for profile icon)
 * @returns Record of panel definitions keyed by panel ID
 * 
 * Note: Permission filtering happens at render time via `requiredPermission` array,
 * not at definition time. This keeps the definitions pure and consistent.
 */
export function buildNavPanelDefs(
  user: { id: string; image?: string | null } | null
): Record<string, WorkspaceToolPanel> {
  return {
    "chat": {
      id: "chat",
      label: "Chat",
      icon: <MessagesSquareIcon className="w-8" strokeWidth={1.5} />,
      toolTip: "Workspace",
      order: 0,
      panel: <WorkspaceNavPanel />,
      requiredPermission: [USER_WORKSPACES_LIST],
      onNavigatorDriverTrigger: (route) => {
        switch (route.route) {
          case "workspace":
          case "workspace-task-create":
          case "workspace-user-list":
          case "workspace-task-edit":
          case "workspace-task-list":
          case "workspace-settings":
            return true
          default:
            return false
        }
      }
    },
    "agents": {
      id: "agents",
      label: "Agents",
      icon: <BotIcon className="w-8" strokeWidth={1.5} />,
      toolTip: "AI Agents",
      order: 10,
      panel: <AgentsNavPanel2 />,
      requiredPermission: [USER_AGENTS_LIST],
      onNavigatorDriverTrigger: (route) => {
        switch (route.route) {
          case "agent-list":
            return true
          default:
            return false
        }
      }
    },
    "wiki": {
      id: "wiki",
      label: "Wiki",
      icon: <BookOpenIcon className="w-8" strokeWidth={1.5} />,
      toolTip: "Neural Wiki",
      order: 20,
      panel: <WikiNavPanel />,
    },
    "tasks": {
      id: "tasks",
      label: "Tasks",
      icon: <ListTodoIcon className="w-8" strokeWidth={1.5} />,
      toolTip: "Tasks",
      order: 25,
      panel: <TasksNavPanel />,
      requiredPermission: [USER_TASKS_MANAGE],
    },
    "notifications": {
      id: "notifications",
      label: "Notifications",
      icon: <UnreadNotificationsBadge />,
      toolTip: "Notifications Inbox",
      order: 30,
      panel: <NotificationsPanel2 />
    },
    "integrations": {
      id: "integrations",
      label: "Integrations",
      icon: <BlocksIcon className="w-8" strokeWidth={1.5} />,
      toolTip: "Integrations",
      order: 40,
      panel: <IntegrationsPanel2 />,
      requiredPermission: [USER_INTEGRATIONS_MANAGE],
      onNavigatorDriverTrigger: (route) => {
        return route.route === "integration-mcp-create";
      }
    },
    // "apps" panel removed - feature disabled for now
    "admin": {
      id: "admin",
      label: "Admin",
      icon: <ShieldCheckIcon className="w-8" strokeWidth={1.5} />,
      toolTip: "Admin",
      order: 60,
      panel: <AdminNavPanel />,
      requiredPermission: [APP_CAN_ACCESS_BACKEND],
      onNavigatorDriverTrigger: (route) => {
        switch (route.route) {
          case "admin-logs":
          case "admin-perf-analyzer":
          case "admin-users":
          case "admin-roles":
          case "admin-model-profiles":
          case "admin-plans":
            return true
          default:
            return false
        }
      }
    },
    "profile": {
      id: "profile",
      label: "Profile",
      icon: user?.image 
        ? <img className="w-7 h-7 rounded-full" src={user.image} alt="Profile" /> 
        : <CircleUserRoundIcon className="w-7" strokeWidth={1.5} />,
      toolTip: "User Profile",
      order: 70,
      pushDown: true,
      panel: <ProfileNavPanel />
    },
    "online": {
      id: "online",
      label: <OnlineOfflineLabelIndicator />,
      icon: <OnlineOfflineIndicator className="w-5 h-5" />,
      order: 80,
      pushDown: true
    }
  }
}
